<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTimeLocalized } from 'shared/helpers/timeHelper';
import { DuplicateContactException } from 'shared/helpers/CustomErrors';
import ContactAPI from 'dashboard/api/contacts';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactPortfolioOwner from 'dashboard/components-next/Contacts/ContactPortfolioOwner.vue';
import ContactOwnerAssignment from 'dashboard/components-next/Contacts/ContactOwnerAssignment.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';
import Policy from 'dashboard/components/policy.vue';
import TeamNumberBadge from 'dashboard/components/widgets/TeamNumberBadge.vue';
import { isTeamNumberContact } from 'dashboard/helper/teamNumber';
import ContactTeamAssignment from 'dashboard/components-next/Contacts/ContactTeamAssignment.vue';
import ContactTeamBadge from 'dashboard/components-next/Contacts/ContactTeamBadge.vue';
import ContactIdentities from 'dashboard/components-next/Contacts/ContactIdentities.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
  showData: { type: Boolean, default: true },
});

const emit = defineEmits(['goToContactsList', 'openConversation']);

const { t } = useI18n();
const store = useStore();

const confirmDeleteContactDialogRef = ref(null);

const avatarFile = ref(null);
const avatarUrl = ref('');

const contactsFormRef = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

// Aceleres -- o selo também aqui: esta é a tela onde a marcação é feita pelo
// atributo personalizado, e sem ele não havia nenhuma confirmação visível de
// que o contato virou Número da Equipe.
const isTeamNumber = computed(() => isTeamNumberContact(props.selectedContact));

const contactData = ref({});

const getInitialContactData = () => {
  if (!props.selectedContact) return {};
  return { ...props.selectedContact };
};

onMounted(() => {
  Object.assign(contactData.value, getInitialContactData());
});

const createdAt = computed(() => {
  return contactData.value?.createdAt
    ? dynamicTimeLocalized(contactData.value.createdAt)
    : '';
});

const lastActivityAt = computed(() => {
  return contactData.value?.lastActivityAt
    ? dynamicTimeLocalized(contactData.value.lastActivityAt)
    : '';
});

const avatarSrc = computed(() => {
  return avatarUrl.value ? avatarUrl.value : contactData.value?.thumbnail;
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const normalizeEmail = value =>
  String(value || '')
    .trim()
    .toLowerCase();

const linkedAceleresClientId = contact => {
  const attributes =
    contact?.customAttributes || contact?.custom_attributes || {};
  return attributes.aceleresClientId || attributes.aceleres_client_id || null;
};

const updateContact = async () => {
  try {
    const { customAttributes, ...basicContactData } = contactData.value;
    // Portfolio ownership is saved separately by the official assignment API.
    // An initial form snapshot must not overwrite a newly assigned owner.
    if (basicContactData.additionalAttributes) {
      basicContactData.additionalAttributes = {
        ...basicContactData.additionalAttributes,
      };
      delete basicContactData.additionalAttributes.carteiraOwnerId;
      delete basicContactData.additionalAttributes.carteira_owner_id;
    }
    const clientId = linkedAceleresClientId(props.selectedContact);
    const profileEmail = basicContactData.profileEmail || null;
    const currentProfileEmail =
      props.selectedContact.profileEmail ||
      props.selectedContact.profile_email ||
      '';
    const profileEmailChanged =
      normalizeEmail(profileEmail) !== normalizeEmail(currentProfileEmail);

    if (clientId) delete basicContactData.profileEmail;

    // O e-mail cadastral de clientes ligados à Base de Clientes tem a mesma
    // fonte oficial do Laravel. Primeiro salva os campos nativos; só então
    // solicita a alteração oficial, que espelha o valor de volta ao contato.
    await store.dispatch('contacts/update', basicContactData);
    if (clientId && profileEmailChanged) {
      await ContactAPI.updateAceleresClient(props.selectedContact.id, {
        client_id: Number(clientId),
        email: profileEmail,
      });
      await store.dispatch('contacts/show', { id: props.selectedContact.id });
    }

    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
    store
      .dispatch('contacts/fetchContactableInbox', props.selectedContact.id)
      .catch(() => null);
  } catch (error) {
    if (
      error instanceof DuplicateContactException &&
      error.errorCode === 'email_already_taken'
    ) {
      contactsFormRef.value?.setEmailError(error.message);
      return;
    }

    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  }
};

const openConfirmDeleteContactDialog = () => {
  confirmDeleteContactDialogRef.value?.dialogRef.open();
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;

  try {
    await store.dispatch('contacts/update', {
      ...contactsFormRef.value?.state,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    if (props.selectedContact && props.selectedContact.id) {
      await store.dispatch('contacts/deleteAvatar', props.selectedContact.id);
      useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
    }
    avatarFile.value = null;
    avatarUrl.value = '';
    contactData.value.thumbnail = null;
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};
defineExpose({ openConfirmDeleteContactDialog });
</script>

<template>
  <div class="w-full">
    <Teleport to="#contact-detail-profile" defer>
      <div class="flex items-center gap-3 max-md:contents">
        <Avatar
          :src="avatarSrc || ''"
          :name="selectedContact?.name || ''"
          :size="72"
          class="[&>span[role=img]]:!rounded-[20px] max-md:col-start-1 max-md:row-start-1"
          allow-upload
          @upload="handleAvatarUpload"
          @delete="handleAvatarDelete"
        />
        <div class="flex flex-col gap-1 max-md:contents">
          <div class="flex flex-wrap items-center gap-2 max-md:col-span-2 max-md:col-start-2 max-md:row-start-1 max-md:min-w-0 max-md:self-center">
            <h3 class="m-0 text-[24px] font-bold leading-tight text-[#101828] max-md:w-full max-md:break-words max-md:text-xl">
              {{ selectedContact?.name }}
            </h3>
            <TeamNumberBadge v-if="isTeamNumber" />
            <ContactTeamBadge :team="selectedContact?.team" />
          </div>
          <div class="flex flex-wrap items-center gap-2 text-xs max-md:col-span-3 max-md:col-start-1 max-md:row-start-2 max-md:min-w-0 max-md:flex-col max-md:items-start">
            <span
              v-if="selectedContact?.phoneNumber || selectedContact?.identifier"
              class="inline-flex items-center gap-1 text-xs text-n-slate-11 max-md:w-full max-md:min-w-0 max-md:break-all"
            >
              <span
                :class="
                  selectedContact?.phoneNumber
                    ? 'i-lucide-phone'
                    : 'i-ph-user-gear'
                "
                class="text-n-slate-10 size-4 shrink-0"
              />
              {{ selectedContact?.phoneNumber || selectedContact?.identifier }}
            </span>
            <span
              class="inline-flex items-center gap-1 text-xs text-n-slate-11 max-md:w-full"
            >
              <span class="i-lucide-clock text-n-slate-10 size-4 shrink-0 md:hidden" />
              <span
                v-if="selectedContact?.identifier"
                class="i-ph-activity text-n-slate-10 size-4 max-md:hidden"
              />
              {{
                $t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt })
              }}
              <span class="max-md:hidden">
                •
                {{
                  $t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
                    date: lastActivityAt,
                  })
                }}
              </span>
            </span>
            <ContactPortfolioOwner
              :owner="selectedContact?.portfolioOwner"
              details
              class="!text-xs max-md:w-full"
            />
          </div>
          <ContactLabels
            :contact-id="selectedContact?.id"
            class="mt-1 max-md:col-span-2 max-md:col-start-1 max-md:row-start-3 max-md:min-w-0 max-md:mt-0"
          />
        </div>
      </div>
    </Teleport>
    <div v-show="showData" class="flex w-full flex-col gap-3 pb-6">
      <ContactIdentities
        :contact-id="selectedContact?.id"
        :contact-phone="
          selectedContact?.phoneNumber || selectedContact?.phone_number || ''
        "
        detail-view
        @open-conversation="emit('openConversation', $event)"
      />
      <ContactsForm
        ref="contactsFormRef"
        :contact-data="contactData"
        is-details-view
        @update="handleFormUpdate"
      >
        <template #details-attributes>
          <ContactOwnerAssignment :contact="selectedContact" />
          <Policy
            :permissions="['administrator']"
            class="min-w-0 [&>div>div>span:last-child]:hidden [&>div>div>span:first-child]:text-xs [&>div>div>span:first-child]:text-n-slate-11"
          >
            <ContactTeamAssignment :contact="selectedContact" />
          </Policy>
        </template>
      </ContactsForm>
      <Button
        class="self-end !h-10 !rounded-lg !bg-[#10a344] !text-white"
        :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
        size="sm"
        :is-loading="isUpdating"
        :disabled="isUpdating || isFormInvalid"
        @click="updateContact"
      />
    </div>
    <Policy :permissions="['administrator']">
      <ConfirmContactDeleteDialog
        ref="confirmDeleteContactDialogRef"
        :selected-contact="selectedContact"
        @go-to-contacts-list="emit('goToContactsList')"
      />
    </Policy>
  </div>
</template>
